import React, { useState, useEffect, useMemo, useRef } from 'react';
import { documentAPI } from '../../api/api';
import { summarizeMakbuzBelgeleri } from '../../utils/makbuzHesap';
import { makbuzOku } from '../../utils/makbuzOku';
import './KdvPaneli.css';

/**
 * Kesilen serbest meslek makbuzlarindan dogan KDV yukumlulugu.
 *
 * Her makbuzun KENDI KDV'si kullanilir (2026-10-07): makbuz yuklenirken
 * makbuzdaki "tahsil edilen KDV" okunup kaydediliyor. Sahsa kesilen makbuzda
 * stopaj ve KDV tevkifati yoktur, KDV'nin tamami odenir; sigortaya kesilende
 * yarisi alicida kalir. KDV'si kayitli olmayan (eski) makbuzda net tutardan
 * varsayilan oranlarla (stopaj %20, tevkifat %50) tahmin edilir; "PDF'lerinden
 * oku" dugmesi o makbuzlarin KDV'sini PDF'ten okuyup kaydeder.
 *
 * Indirilecek KDV (alislar) hesaba katilmaz; gercek beyanname tutari bundan
 * dusuk cikabilir.
 */

const AY_ADLARI = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const paraFormat = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 })
    .format(Number(n) || 0);

const kisaPara = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(1).replace('.', ',') + ' M';
  if (v >= 1000) return Math.round(v / 1000) + ' B';
  return v > 0 ? String(Math.round(v)) : '';
};

const belgeNo = (m) => m.Id ?? m.id;
const kdvKayitli = (m) => (m.OdenecekKdv ?? m.odenecekKdv) != null;
const pdfMakbuz = (m) => /pdf/i.test(m.ContentType ?? m.contentType ?? '') || /\.pdf$/i.test(m.FileName ?? m.fileName ?? '');

const KdvPaneli = ({ yil, ay }) => {
  const [makbuzlar, setMakbuzlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');
  const [okuma, setOkuma] = useState(null); // { yapilan, toplam, okunan, okunamayan, bitti }
  const iptalRef = useRef(false);

  useEffect(() => {
    let iptal = false;
    (async () => {
      setYukleniyor(true);
      try {
        const veri = await documentAPI.getInvoices();
        if (!iptal) setMakbuzlar(Array.isArray(veri) ? veri : []);
      } catch (e) {
        if (!iptal) setHata(e.response?.data?.message || e.message || 'Makbuzlar yüklenemedi.');
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    })();
    return () => { iptal = true; iptalRef.current = true; };
  }, []);

  /** Makbuzları tarihine göre yıl/ay kovalarına ayırır. */
  const donemler = useMemo(() => {
    const aylik = new Map();       // "yil-ay" -> makbuz belgeleri
    for (const m of makbuzlar) {
      const ham = m.FaturaTarihi ?? m.faturaTarihi ?? m.CreatedDate ?? m.createdDate;
      const tutar = Number(m.Tutar ?? m.tutar) || 0;
      if (!ham || tutar <= 0) continue;
      const t = new Date(ham);
      if (Number.isNaN(t.getTime())) continue;
      const anahtar = t.getFullYear() + '-' + (t.getMonth() + 1);
      if (!aylik.has(anahtar)) aylik.set(anahtar, []);
      aylik.get(anahtar).push(m);
    }
    return aylik;
  }, [makbuzlar]);

  const ayOzet = useMemo(
    () => summarizeMakbuzBelgeleri(donemler.get(yil + '-' + ay) || []),
    [donemler, yil, ay]
  );

  const yilSerisi = useMemo(
    () => Array.from({ length: 12 }, (_, i) => {
      const ozet = summarizeMakbuzBelgeleri(donemler.get(yil + '-' + (i + 1)) || []);
      return { ay: i + 1, odenecekKdv: ozet.odenecekKdv, adet: ozet.count, tahmini: ozet.tahmini };
    }),
    [donemler, yil]
  );

  const yilToplam = useMemo(
    () => yilSerisi.reduce((t, x) => t + x.odenecekKdv, 0),
    [yilSerisi]
  );

  // Seçili yılda KDV'si kayıtlı olmayan makbuzlar (tahmin edilenler)
  const tahminiMakbuzlar = useMemo(
    () => Array.from({ length: 12 }, (_, i) => donemler.get(yil + '-' + (i + 1)) || [])
      .flat()
      .filter((m) => !kdvKayitli(m)),
    [donemler, yil]
  );

  /** KDV'si kayıtlı olmayan makbuzların PDF'ini okur; bulunan KDV'yi kaydeder. */
  const pdflerdenOku = async () => {
    const liste = tahminiMakbuzlar.filter(pdfMakbuz);
    iptalRef.current = false;
    let okunan = 0;
    let okunamayan = tahminiMakbuzlar.length - liste.length; // PDF olmayanlar okunamaz
    setOkuma({ yapilan: 0, toplam: liste.length, okunan, okunamayan, bitti: false });
    for (let i = 0; i < liste.length; i++) {
      if (iptalRef.current) break;
      const m = liste[i];
      try {
        const { blob } = await documentAPI.download(belgeNo(m));
        const ad = m.FileName ?? m.fileName ?? 'makbuz.pdf';
        const s = await makbuzOku(new File([blob], ad, { type: 'application/pdf' }));
        if (s.odenecekKdv != null) {
          const guncel = await documentAPI.makbuzKdv(belgeNo(m), { odenecekKdv: s.odenecekKdv, brutUcret: s.brutUcret ?? null });
          setMakbuzlar((o) => o.map((x) => (belgeNo(x) === belgeNo(m)
            ? { ...x, OdenecekKdv: guncel?.OdenecekKdv ?? s.odenecekKdv, BrutUcret: guncel?.BrutUcret ?? s.brutUcret ?? null }
            : x)));
          okunan += 1;
        } else {
          okunamayan += 1;
        }
      } catch (e) {
        okunamayan += 1;
      }
      setOkuma({ yapilan: i + 1, toplam: liste.length, okunan, okunamayan, bitti: false });
    }
    setOkuma({ yapilan: liste.length, toplam: liste.length, okunan, okunamayan, bitti: true });
  };

  if (yukleniyor) return <div className="kdv-durum">KDV hesabı yükleniyor…</div>;
  if (hata) return <div className="kdv-durum kdv-hata">{hata}</div>;

  const enBuyuk = Math.max(1, ...yilSerisi.map((x) => x.odenecekKdv));
  const okunuyor = okuma && !okuma.bitti;

  return (
    <div className="kdv">
      <div className="kdv-ust">
        <div className="kdv-ana">
          <span className="kdv-ana-ad">{AY_ADLARI[ay - 1]} ayı ödenecek KDV</span>
          <strong className="kdv-ana-tutar">{paraFormat(ayOzet.odenecekKdv)}</strong>
          <span className="kdv-ana-alt">
            {ayOzet.count > 0
              ? ayOzet.count + ' makbuz · ' + paraFormat(ayOzet.netTutar) + ' net tahsilat'
              : 'Bu ay makbuz kesilmemiş'}
          </span>
          {ayOzet.tahmini > 0 && (
            <span className="kdv-ana-tahmin">
              {ayOzet.tahmini === ayOzet.count ? 'Tamamı' : ayOzet.tahmini + ' makbuz'} tahmini
            </span>
          )}
        </div>

        <dl className="kdv-kalemler">
          <div>
            <dt>Brüt ücret (KDV hariç)</dt>
            <dd>{paraFormat(ayOzet.brutUcret)}</dd>
          </div>
          <div>
            <dt>Hesaplanan KDV (%20)</dt>
            <dd>{paraFormat(ayOzet.kdv)}</dd>
          </div>
          <div>
            <dt>KDV tevkifatı (alıcıda kalan)</dt>
            <dd className="kdv-eksi">− {paraFormat(ayOzet.kdvTevkifat)}</dd>
          </div>
          <div className="kdv-vurgu">
            <dt>Tahsil edilen = ödenecek KDV</dt>
            <dd>{paraFormat(ayOzet.odenecekKdv)}</dd>
          </div>
          <div>
            <dt>G.V. stopajı (alıcıda)</dt>
            <dd>{paraFormat(ayOzet.gvStopaj)}</dd>
          </div>
        </dl>
      </div>

      {tahminiMakbuzlar.length > 0 && (
        <div className="kdv-tahmin">
          <p>
            {yil} yılında <strong>{tahminiMakbuzlar.length} makbuzun</strong> KDV'si makbuzdan okunmamış;
            net tutardan tahmin edildi (stopaj %20, KDV'nin yarısı tevkif varsayımı). Şahsa kesilen
            makbuzlarda bu tahmin eksik çıkar.
          </p>
          {okuma && (
            <p className="kdv-tahmin-durum" role="status">
              {okunuyor
                ? `PDF'ler okunuyor… ${okuma.yapilan} / ${okuma.toplam}`
                : `${okuma.okunan} makbuzun KDV'si okundu ve kaydedildi.${okuma.okunamayan > 0 ? ` ${okuma.okunamayan} makbuzda KDV bulunamadı (taranmış, PDF değil ya da KDV satırı yok); onlar tahmini kalır.` : ''}`}
            </p>
          )}
          {!okunuyor && tahminiMakbuzlar.some(pdfMakbuz) && (
            <button type="button" className="kdv-oku-dugme" onClick={pdflerdenOku}>
              KDV'yi PDF'lerinden oku
            </button>
          )}
          {okunuyor && (
            <button type="button" className="kdv-oku-dugme kdv-oku-dur" onClick={() => { iptalRef.current = true; }}>
              Durdur
            </button>
          )}
        </div>
      )}

      <div className="kdv-yil">
        <header className="kdv-yil-basi">
          <h4>{yil} yılı aylık ödenecek KDV</h4>
          <span className="kdv-yil-toplam">{paraFormat(yilToplam)}</span>
        </header>
        <div className="kdv-grafik">
          {yilSerisi.map((x) => {
            const yuzde = (x.odenecekKdv / enBuyuk) * 100;
            return (
              <div
                key={x.ay}
                className={'kdv-sutun' + (x.ay === ay ? ' secili' : '')}
                title={AY_ADLARI[x.ay - 1] + ': ' + paraFormat(x.odenecekKdv) + ' (' + x.adet + ' makbuz'
                  + (x.tahmini > 0 ? ', ' + x.tahmini + ' tahmini' : '') + ')'}
              >
                <span className="kdv-sutun-deger">{kisaPara(x.odenecekKdv)}</span>
                <div
                  className="kdv-sutun-cubuk"
                  style={{ height: Math.max(yuzde, x.odenecekKdv > 0 ? 3 : 0) + '%' }}
                />
                <span className="kdv-sutun-ay">{AY_ADLARI[x.ay - 1].slice(0, 3)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="kdv-not">
        Her makbuzun KDV'si makbuzun kendisinden alınır; makbuz eklerken PDF'ten okunur.
        Alışlarınızdan doğan indirilecek KDV hesaba katılmaz; beyannamedeki tutar bundan düşük çıkabilir.
      </p>
    </div>
  );
};

export default KdvPaneli;
